import type { ComponentType } from 'react';
import { create } from 'zustand';

/** Mirrors the `MatDialogConfig` options actually used across the app. */
export interface DialogOptions {
  /** Literal px width: 400 | 550 | 600 | 700 | 750 | 800 | 900 are all in use. */
  width?: number;
  /** `maxWidth: '90vw' | '95vw'`. */
  maxWidthVw?: number;
  /** `maxHeight`, e.g. 90 for '90vh'. */
  maxHeightVh?: number;
  /** `disableClose: true` — blocks Escape and backdrop dismissal. */
  disableClose?: boolean;
  /** `panelClass` — kept so ported dialog CSS still applies. */
  panelClass?: string;
  /** Set false to skip autofocus (Angular's `autoFocus`). */
  autoFocus?: boolean;
}

/** Props every dialog component receives. Replaces MAT_DIALOG_DATA + MatDialogRef. */
export interface DialogComponentProps<TData, TResult> {
  data: TData;
  /** Resolves the `openDialog` promise. Calling with no argument = cancelled. */
  close: (result?: TResult) => void;
}

interface DialogEntry {
  id: string;
  // The host renders heterogeneous dialog components; their prop types are checked
  // at the `openDialog` boundary, which is where type safety belongs.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<DialogComponentProps<any, any>>;
  data: unknown;
  options: DialogOptions;
  resolve: (result: unknown) => void;
  /** Set while the exit animation plays, before the entry is removed. */
  closing?: boolean;
}

interface DialogState {
  stack: DialogEntry[];
  push: (entry: DialogEntry) => void;
  /** Marks an entry closing and resolves its promise. */
  requestClose: (id: string, result: unknown) => void;
  closeAll: () => void;
}

/**
 * Grace period between flipping a dialog to `closing` and dropping it from the stack.
 *
 * Radix's Presence keeps `Dialog.Content` mounted until its CSS exit animation ends,
 * so the entry must outlive `open={false}` or the component unmounts mid-animation and
 * the panel disappears with a visible pop. Must stay >= the longest exit animation in
 * keyframes.css (`vm-content-out`, 120ms).
 */
const EXIT_ANIMATION_MS = 200;

export const useDialogStore = create<DialogState>((set, get) => ({
  stack: [],

  push: (entry) => set((state) => ({ stack: [...state.stack, entry] })),

  requestClose: (id, result) => {
    const entry = get().stack.find((item) => item.id === id);
    if (!entry || entry.closing) return;

    // Resolve immediately so `await openDialog(...)` continues without waiting for
    // the exit animation. The entry lingers only so Radix can animate it out.
    entry.resolve(result);
    set((state) => ({
      stack: state.stack.map((item) => (item.id === id ? { ...item, closing: true } : item)),
    }));

    setTimeout(() => {
      set((state) => ({ stack: state.stack.filter((item) => item.id !== id) }));
    }, EXIT_ANIMATION_MS);
  },

  closeAll: () => {
    for (const entry of get().stack) {
      if (!entry.closing) entry.resolve(undefined);
    }
    set({ stack: [] });
  },
}));
